/**
 * Result of a 20-year Treasury real-yield lookup.
 */
export interface TreasuryYieldData {
  /** Observation date (YYYY-MM-DD), or today's date when the fetch failed. */
  date: string;
  /** Annual rate as a decimal (0.025 = 2.5%); the 2.5% default on failure. */
  rate: number;
  /** True only when `rate` came from a live feed. */
  success: boolean;
  /** Set only on failure. */
  error?: string;
}

/**
 * Formats a date as YYYYMM for use in the Treasury API URL
 * @param date - The date to format
 * @returns Formatted date string (YYYYMM)
 */
function formatDateForTreasuryAPI(date: Date, monthOffset: number = 0): string {
  const d = new Date(date);
  d.setMonth(d.getMonth() + monthOffset);
  const year = d.getFullYear();
  const month = d.getMonth() + 1; // Month is 0-indexed in JavaScript
  return `${year}${month.toString().padStart(2, '0')}`;
}

/**
 * Fetches and parses Treasury yield XML for a given month offset.
 */
async function fetchTreasuryMonth(
  date: Date,
  monthOffset: number
): Promise<{ response: Response; entries: HTMLCollectionOf<Element> }> {
  const formattedDate = formatDateForTreasuryAPI(date, monthOffset);
  const url = `https://home.treasury.gov/resource-center/data-chart-center/interest-rates/pages/xml?data=daily_treasury_real_yield_curve&field_tdr_date_value_month=${formattedDate}`;
  const response = await fetch(url);
  const xmlText = await response.text();
  const xmlDoc = new DOMParser().parseFromString(xmlText, 'text/xml');
  const entries = xmlDoc.getElementsByTagName('entry');
  return { response, entries };
}

/**
 * Fetches the latest 20-year Treasury yield from the Treasury Department API
 * @returns Promise resolving to the yield data; `success` is false on failure
 */
export async function fetchLatest20YearTreasuryYield(): Promise<TreasuryYieldData> {
  try {
    const currentDate = new Date();

    // Try current month first, then fall back to previous month.
    // Early in the month, Treasury may not have published data yet.
    let { response, entries } = await fetchTreasuryMonth(currentDate, 0);
    if (entries.length === 0) {
      console.warn(
        'No Treasury data for current month, trying previous month.'
      );
      ({ response, entries } = await fetchTreasuryMonth(currentDate, -1));
    }

    if (!response.ok) {
      throw new Error(
        `Failed to fetch Treasury data: ${response.status} ${response.statusText}`
      );
    }

    if (entries.length === 0) {
      throw new Error(
        'No Treasury yield data found for current or previous month.'
      );
    }

    let latestDate: string | null = null;
    let latestYield: number | null = null;

    for (let i = 0; i < entries.length; i++) {
      const entry = entries[i];
      const properties = entry.getElementsByTagName('m:properties');

      if (properties.length > 0) {
        const dateElement = properties[0].getElementsByTagName('d:NEW_DATE');
        const yieldElement = properties[0].getElementsByTagName('d:TC_20YEAR');

        if (
          dateElement.length > 0 &&
          dateElement[0].textContent &&
          yieldElement.length > 0 &&
          yieldElement[0].textContent
        ) {
          const currentDateStr = dateElement[0].textContent;
          const currentYieldVal = parseFloat(yieldElement[0].textContent);

          // Skip entries with invalid yield values
          if (Number.isNaN(currentYieldVal)) {
            continue;
          }

          // Compare dates to find the latest one
          if (!latestDate || currentDateStr > latestDate) {
            latestDate = currentDateStr;
            latestYield = currentYieldVal;
          }
        }
      }
    }

    if (latestDate && latestYield !== null) {
      const yieldDecimal = latestYield / 100;
      return {
        date: latestDate,
        rate: yieldDecimal,
        success: true,
      };
    } else {
      throw new Error('20-year Treasury yield data not found in the response');
    }
  } catch (error) {
    console.error('Error fetching Treasury yield data:', error);
    return {
      date: new Date().toISOString().split('T')[0],
      rate: 0.025, // Default to 2.5% if there's an error
      success: false,
      error: error.message,
    };
  }
}

/**
 * Fetches the latest 20-year inflation-adjusted Treasury yield from FRED (Federal Reserve Economic Data).
 * @returns Promise resolving to the yield data; `success` is false on failure
 */
export async function fetchFredDFII20Yield(): Promise<TreasuryYieldData> {
  try {
    const fredResponse = await fetch(
      'https://fred.stlouisfed.org/graph/fredgraph.csv?id=DFII20'
    );

    if (!fredResponse.ok) {
      throw new Error(
        `Failed to fetch FRED data: ${fredResponse.status} ${fredResponse.statusText}`
      );
    }

    const csvText = await fredResponse.text();
    const lines = csvText.trim().split('\n');

    // Iterate backward from the end to find the most recent valid data point
    for (let i = lines.length - 1; i >= 1; i--) {
      // Skip header line (index 0)
      const line = lines[i];
      const [date, rateStr] = line.split(',');
      const rate = parseFloat(rateStr);

      if (!Number.isNaN(rate) && rateStr.trim() !== '') {
        // Found a valid rate
        return {
          date: date,
          rate: rate / 100,
          success: true,
        };
      }
    }
    throw new Error('FRED DFII20 data not found or is invalid in the response');
  } catch (error) {
    console.error('Error fetching FRED DFII20 data:', error);
    return {
      date: new Date().toISOString().split('T')[0],
      rate: 0.025, // Default to 2.5% if there's an error
      success: false,
      error: error.message,
    };
  }
}

/**
 * Fetches the current 20-year inflation-adjusted Treasury yield, trying the
 * Treasury Department feed first and FRED second. Never throws: when both
 * sources fail, `success` is false and `rate` is the 2.5% default.
 * @returns Promise resolving to the yield data, with `success` reporting
 *   whether a live rate was obtained.
 */
export async function fetchRecommendedDiscountRate(): Promise<TreasuryYieldData> {
  const treasuryData = await fetchLatest20YearTreasuryYield();
  if (treasuryData.success) {
    return treasuryData;
  }
  console.warn(
    'Using fallback due to Treasury data error:',
    treasuryData.error
  );

  const fredData = await fetchFredDFII20Yield();
  if (!fredData.success) {
    console.warn(
      'Using default discount rate due to FRED data error:',
      fredData.error
    );
  }
  return fredData;
}

/**
 * Gets the current recommended discount rate based on Treasury yields
 * Falls back to FRED data, then to a default value of 2.5% if unable to fetch the data.
 * @returns Promise resolving to the recommended discount rate as a decimal
 */
export async function getRecommendedDiscountRate(): Promise<number> {
  try {
    return (await fetchRecommendedDiscountRate()).rate;
  } catch (error) {
    console.error('Error getting recommended discount rate:', error);
    return 0.025; // Default to 2.5% if there's an error
  }
}
